import { makeAutoObservable } from 'mobx';

import { apiFetch } from 'framework/api';
import { newGuid } from 'tools/guid';
import { MobxManager } from 'framework/mobx-integration';
import { DataSourceManager } from 'controls/designer/features/widgets/datasources/shared/utils/data-source-manager/dataSourceManager';
import { DataSourceSelection } from 'areas/data-sources/types/dataSource';
import { AntSelectOption } from 'controls/react/ant/antSelect';
import { clearDatasourceCache } from 'controls/designer/features/widgets/datasources/api';

import { DataSourceSingleGraphDataSourceConfig, DataSourceSingleGraphWidgetConfig } from '../types';

class RowSelection implements DataSourceSelection {
	constructor(
		private root: DataSourceSingleGraphWidgetConfig,
		private row: DataSourceSingleGraphDataSourceConfig,
	) {}

	get accountId() {
		return this.root.accountId;
	}

	get dataSourceSelectorAccountId() {
		return this.root.dataSourceSelectorAccountId;
	}
	set dataSourceSelectorAccountId(value: string | null) {
		this.root.dataSourceSelectorAccountId = value;
	}

	get dataSourceId() {
		return this.row.dataSourceId;
	}
	set dataSourceId(value: string) {
		this.row.dataSourceId = value;
	}

	get rowKey() {
		return this.row.rowKey;
	}
	set rowKey(value: string) {
		this.row.rowKey = value;
	}

	get rowKeyValue() {
		return this.row.rowKeyValue;
	}
	set rowKeyValue(value: string) {
		this.row.rowKeyValue = value;
	}
}

export class DataSourceSingleGraphRowStore {
	manager: DataSourceManager;
	requestId = newGuid();
	mobx = new MobxManager();

	get row() {
		return this.props.row;
	}

	get accountId() {
		return this.props.root.accountId;
	}

	get currentDataSource() {
		return this.manager.selectedDataSource;
	}

	get dataSourceOptions(): AntSelectOption[] {
		return this.manager.dataSourcesOptions ?? [];
	}

	constructor(public props: { root: DataSourceSingleGraphWidgetConfig; row: DataSourceSingleGraphDataSourceConfig }) {
		makeAutoObservable(this);

		this.manager = new DataSourceManager({
			selection: new RowSelection(props.root, props.row),
			loadDatasourceList: true,
			requestId: this.requestId,
		});

		this.mobx.reaction(
			() => this.row.dataSourceId,
			() => {
				this.row.timeColumn = undefined;
				this.row.metricColumn = undefined;
				this.row.metricRowName = undefined;
				this.row.rowKey = undefined;
				this.row.rowKeyValue = undefined;
			},
		);

		this.mobx.reaction(
			() => this.currentDataSource,
			(dataSource) => {
				this.row.datasourceName = dataSource?.name;
			},
			{ fireImmediately: true },
		);

		this.mobx.reaction(
			() => [this.row.metricColumn, this.manager.rulesConfiguration.data] as const,
			([metricColumn, rulesData]) => {
				this.row.metricRowName = metricColumn ? (rulesData?.[metricColumn]?.label ?? metricColumn) : undefined;
			},
			{ fireImmediately: true },
		);
	}

	destroy() {
		this.mobx.destroy();
		this.manager.destroy();
		void apiFetch(clearDatasourceCache({ accountId: this.props.root.accountId, requestId: this.requestId }));
	}
}
